// calculatePennyDifference — faithful port of OrderCreateViewModel.kt:4682-5079
// plus validatePercentageDiscount/validateAmountDiscount/validateLpDiscount (1279-1414, 1227-1277).
//
// Distributes order-level discounts across lines and penny-reconciles so per-line sums stay exact.
// Sets per line: jsonNewPrice (final net), jsonCouponCodeAmt1 (item + order-% share),
// jsonCouponCodeAmt2 (order-$ share), jsonLoyaltyAmt, customDiscountAmt, couponDiscountAmt.
//
// Android represents order-level discounts as synthetic cart lines; here they are explicit opts:
//   mSubTotal            - authoritative subtotal (from calculateSubtotal)
//   orderPercentDiscount - $ value of a %-type order discount (Stage A proration)
//   orderAmountDiscount  - $ value of a $-type order discount (Stage B proration)
//   couponTotal          - $ of coupon-type order discount (Stage E split)
//   customTotal          - $ of custom-type order discount (Stage E split)
//   loyaltyAmount        - $ loyalty redemption (Stage C; Phase 2, pass 0 for v1)
//   isOtdCoupon          - the coupon-kind discount is the synthetic OTD (keeps full-cart spread)
//
// exclude_coupons_store (Android 2026-06-05 + refinements): lines whose category is flagged
// exclude-coupons carry isCouponEligible=false. A REAL list coupon then (a) computes over the
// eligible-only base, (b) distributes only onto eligible lines, and (c) penny-reconciles only
// within the eligible pool (bounded extra ±0.01 passes — a smaller pool may need more than one).
// Custom discounts and OTD intentionally keep the FULL-cart spread (Android getDiscountAmount).

import { roundHalfUp, truncate2, toNum } from "./money";
import { deziNetSubtotal, isDeziLine } from "./dezi";

const nz = (v) => {
  const n = toNum(v);
  return isNaN(n) ? 0 : n;
};
// addAndRoundIfNeeded (OCV 6022): "%.2f" round only if it changes the value → 2dp half-up.
const addAndRoundIfNeeded = (v) => roundHalfUp(v);
const descByNewPrice = (a, b) => nz(b.jsonNewPrice) - nz(a.jsonNewPrice);
const descByUnitNew = (a, b) => toNum(b.orderUnitNewPrice) - toNum(a.orderUnitNewPrice);

export function getCartListPenny(cart) {
  // Android (OCV:1453-1458) excludes lottery/scratcher payout + lottery-ticket-buy lines from ALL
  // penny + order-discount proration — they must not receive prorated discounts.
  return cart.filter(
    (l) =>
      !l.isSplitPaymentDiscount &&
      !l.isSplitPayment &&
      !l.isSplitRefund &&
      !l.isScratcherPayout &&
      !l.isLotteryPayout &&
      l.isLotteryTicket !== "1"
  );
}

function lotteryScratcherSum(cart) {
  return cart
    .filter((l) => l.isLotteryPayout || l.isScratcherPayout || l.isLotteryTicket === "1")
    .reduce((s, l) => s + toNum(l.orderUnitPrice), 0);
}

// exclude_coupons_store helpers — a line defaults to eligible (legacy lines have no flag).
const isEligible = (l) => l.isCouponEligible !== false;
export const hasCouponExcludedItems = (cart) => getCartListPenny(cart).some((l) => !isEligible(l));

// couponEligibleSubtotal (OCV 3663): subtotal of coupon-eligible penny lines only — the same math
// as the authoritative subtotal ($ item discount ×count, % item discount per-unit truncated), so a
// fully-eligible cart returns mSubTotal − lotteryBuyAmount.
export function couponEligibleSubtotal(cart) {
  return getCartListPenny(cart)
    .filter((l) => isEligible(l))
    .reduce((s, l) => {
      const unit = toNum(l.orderUnitNewPrice) > 0 ? toNum(l.orderUnitNewPrice) : toNum(l.orderUnitPrice);
      const count = toNum(l.orderCount);
      const pct = toNum(l.orderDiscountPercent);
      const itemDisc = toNum(l.orderDiscount) > 0 ? toNum(l.orderDiscount) * count : pct > 0 ? truncate2((unit * pct) / 100) : 0;
      return s + (unit * count - itemDisc);
    }, 0);
}

function validatePercentageDiscount(cart, mSubTotal, orderPercentageDiscount, couponOnlyPool = false, deziUpcSet = null) {
  const list = getCartListPenny(cart);
  const lineItemSubtotal = roundHalfUp(list.reduce((s, l) => s + nz(l.jsonNewPrice), 0));
  const lotteryTicketSum = cart.filter((l) => l.isLotteryTicket === "1").reduce((s, l) => s + toNum(l.orderUnitPrice), 0);
  const newSubTotal = roundHalfUp(mSubTotal - orderPercentageDiscount - lotteryTicketSum);
  const pennyAmount = roundHalfUp(lineItemSubtotal - newSubTotal);
  let remaining = pennyAmount;
  // NOTE: Android compares lineItemSubtotal to orderPercentageDiscount here (a quirk); replicated verbatim.
  if (lineItemSubtotal !== orderPercentageDiscount && orderPercentageDiscount !== 0) {
    // exclude_coupons_store: a coupon-only % discount may only ±0.01-adjust coupon-eligible lines;
    // the smaller pool may need more than one pass (Android adjustPass < 8, exclusion case only).
    // A dezi line is NEVER ±0.01-adjusted (rule 4) — it holds no order-level share to adjust.
    const hasDezi = !!(deziUpcSet && deziUpcSet.size > 0);
    const adjustList = (couponOnlyPool ? list.filter(isEligible) : list).filter((l) => !isDeziLine(l, deziUpcSet));
    const sorted = [...adjustList].sort(descByNewPrice);
    let pass = 0;
    do {
      for (const it of sorted) {
        if (remaining === 0) break;
        if (nz(it.jsonNewPrice) >= 0.01) {
          if (pennyAmount < 0) {
            it.jsonNewPrice = addAndRoundIfNeeded(nz(it.jsonNewPrice) + 0.01);
            it.jsonCouponCodeAmt1 = addAndRoundIfNeeded(nz(it.jsonCouponCodeAmt1) - 0.01);
            remaining = roundHalfUp(remaining + 0.01);
          } else {
            it.jsonNewPrice = addAndRoundIfNeeded(nz(it.jsonNewPrice) - 0.01);
            it.jsonCouponCodeAmt1 = addAndRoundIfNeeded(nz(it.jsonCouponCodeAmt1) + 0.01);
            remaining = roundHalfUp(remaining - 0.01);
          }
        }
      }
      pass++;
    } while ((couponOnlyPool || hasDezi) && remaining !== 0 && pass < 8 && sorted.length > 0);
  }
}

function validateAmountDiscount(cart, mSubTotal, orderPercentageDiscount, orderAmountDiscount, couponOnlyPool = false, deziUpcSet = null) {
  const list = getCartListPenny(cart);
  const lineItemSubtotal = roundHalfUp(list.reduce((s, l) => s + nz(l.jsonNewPrice), 0));
  const lotteryTicketSum = cart.filter((l) => l.isLotteryTicket === "1").reduce((s, l) => s + toNum(l.orderUnitPrice), 0);
  const newSubTotal = roundHalfUp(mSubTotal - orderPercentageDiscount - orderAmountDiscount - lotteryTicketSum);
  const pennyAmount = roundHalfUp(lineItemSubtotal - newSubTotal);
  let remaining = pennyAmount;
  if (lineItemSubtotal !== newSubTotal && orderAmountDiscount !== 0) {
    // exclude_coupons_store: coupon-only $ discount adjusts eligible lines only (bounded passes).
    // A dezi line is NEVER ±0.01-adjusted (rule 4) — it holds no order-level share to adjust.
    const hasDezi = !!(deziUpcSet && deziUpcSet.size > 0);
    const adjustList = (couponOnlyPool ? list.filter(isEligible) : list).filter((l) => !isDeziLine(l, deziUpcSet));
    const sorted = [...adjustList].sort(descByNewPrice);
    let pass = 0;
    do {
      for (const it of sorted) {
        if (remaining === 0) break;
        if (nz(it.jsonNewPrice) >= 0.01) {
          if (pennyAmount < 0) {
            it.jsonNewPrice = addAndRoundIfNeeded(nz(it.jsonNewPrice) + 0.01);
            it.jsonCouponCodeAmt2 = addAndRoundIfNeeded(nz(it.jsonCouponCodeAmt2) - 0.01);
            remaining = roundHalfUp(remaining + 0.01);
          } else {
            it.jsonNewPrice = addAndRoundIfNeeded(nz(it.jsonNewPrice) - 0.01);
            it.jsonCouponCodeAmt2 = addAndRoundIfNeeded(nz(it.jsonCouponCodeAmt2) + 0.01);
            remaining = roundHalfUp(remaining - 0.01);
          }
        }
      }
      pass++;
    } while ((couponOnlyPool || hasDezi) && remaining !== 0 && pass < 8 && sorted.length > 0);
  }
}

function validateLpDiscount(cart, mSubTotal, orderPercentageDiscount, orderAmountDiscount, loyaltyAmount) {
  const list = getCartListPenny(cart);
  const lineItemSubtotal = roundHalfUp(list.reduce((s, l) => s + nz(l.jsonNewPrice), 0));
  const newSubTotal = roundHalfUp(mSubTotal - orderPercentageDiscount - orderAmountDiscount - loyaltyAmount);
  const pennyAmount = roundHalfUp(lineItemSubtotal - newSubTotal);
  let remaining = pennyAmount;
  if (lineItemSubtotal !== newSubTotal && loyaltyAmount !== 0) {
    const sorted = list.filter((l) => l.canUsePoint).sort(descByNewPrice);
    for (const it of sorted) {
      if (remaining === 0) break;
      if (nz(it.jsonNewPrice) >= 0.01) {
        if (pennyAmount < 0) {
          it.jsonNewPrice = addAndRoundIfNeeded(nz(it.jsonNewPrice) + 0.01);
          it.jsonLoyaltyAmt = roundHalfUp(addAndRoundIfNeeded(nz(it.jsonLoyaltyAmt) - 0.01));
          remaining = roundHalfUp(remaining + 0.01);
        } else {
          it.jsonNewPrice = addAndRoundIfNeeded(nz(it.jsonNewPrice) - 0.01);
          it.jsonLoyaltyAmt = roundHalfUp(addAndRoundIfNeeded(nz(it.jsonLoyaltyAmt) + 0.01));
          remaining = roundHalfUp(remaining - 0.01);
        }
      }
    }
  }
}

export function calculatePennyDifference(cart, opts = {}) {
  const {
    mSubTotal = 0,
    orderPercentDiscount = 0,
    orderAmountDiscount = 0,
    couponTotal = 0,
    customTotal = 0,
    loyaltyAmount = 0,
    isOtdCoupon = false,
    deziUpcSet = null, // QV-7124 rule 4: dezi lines take NO order-level discount share
    isDual = false, // QV-4241 dual price — points-paid lines waive their NCA (Android §64)
    toBase = (v) => roundHalfUp(v),
  } = opts;

  const opd = roundHalfUp(orderPercentDiscount);
  const oad = orderAmountDiscount;

  // exclude_coupons_store flags (OCV 5970-5990). Order discounts STACK like Android's split-discount
  // lines, so the caller says which part is a real LIST coupon: `percentIsRealCoupon` (the % line's
  // name is in the coupon list) and `couponDollarDiscount` (Σ of the $ lines that are list coupons,
  // 0 under OTD). Legacy callers that pass neither get the old XOR reading (couponTotal ⇒ coupon).
  const hasExcluded = hasCouponExcludedItems(cart);
  const isRealCoupon = roundHalfUp(couponTotal) !== 0 && !isOtdCoupon;
  const pctIsCoupon = opts.percentIsRealCoupon === undefined ? isRealCoupon : !!opts.percentIsRealCoupon;
  const couponDollarDiscount = isOtdCoupon ? 0 : opts.couponDollarDiscount === undefined ? (isRealCoupon ? oad : 0) : toNum(opts.couponDollarDiscount);
  const percentCouponExclusion = hasExcluded && !isOtdCoupon && pctIsCoupon && opd !== 0;
  const dollarCouponExclusion = hasExcluded && couponDollarDiscount !== 0;
  const eligibleSubtotalForCoupon = hasExcluded ? couponEligibleSubtotal(cart) : 0;

  // Stage A: item-level discount + order-level % proration. jsonNewPrice is a LINE total (unit*count),
  // so it stays consistent with the per-line subtotal for any orderCount. The % item discount is
  // per-unit (truncate2, NOT *count) to preserve the Android subtotal quirk; the $ item discount IS *count.
  for (const it of getCartListPenny(cart)) {
    const unit = toNum(it.orderUnitNewPrice) > 0 ? toNum(it.orderUnitNewPrice) : toNum(it.orderUnitPrice);
    const count = toNum(it.orderCount);
    const lineBase = unit * count;
    const pct = toNum(it.orderDiscountPercent);
    const itemDiscountAmount = pct > 0 ? truncate2((unit * pct) / 100) : toNum(it.orderDiscount) * count;
    const afterItem = roundHalfUp(lineBase - itemDiscountAmount);
    // exclude_coupons_store: a % list-coupon spreads over coupon-eligible lines only (OCV 4783-4793)
    // QV-7124 rule 4 ("Decision b"): dezi lines leave the base too, so the order % was computed on the
    // NON-dezi subtotal upstream and dividing by the non-dezi base here yields the exact per-line
    // share. With no dezi items the subtraction is 0 ⇒ the base is byte-identical.
    const baseBeforeDezi =
      percentCouponExclusion && eligibleSubtotalForCoupon > 0
        ? eligibleSubtotalForCoupon
        : mSubTotal - lotteryScratcherSum(cart);
    const newSubTotal = Math.max(baseBeforeDezi - deziNetSubtotal(cart, deziUpcSet, percentCouponExclusion), 0);
    const orderPercentage = newSubTotal > 0 ? opd / newSubTotal : 0;
    const pctShare =
      (percentCouponExclusion && !isEligible(it)) || isDeziLine(it, deziUpcSet) ? 0 : afterItem * orderPercentage;
    const afterOrderPct = roundHalfUp(afterItem - pctShare);
    it.jsonNewPrice = afterOrderPct;
    it.jsonCouponCodeAmt1 = roundHalfUp(lineBase - afterOrderPct);
  }
  validatePercentageDiscount(cart, mSubTotal, opd, percentCouponExclusion, deziUpcSet);

  // Stage B: order-level $ proration
  {
    const list = getCartListPenny(cart);
    // QV-7124 rule 4: the $ discount redistributes over the NON-dezi lines only (it was already capped
    // at the non-dezi base upstream, so the full entered $ still lands — just never on a dezi line).
    // No dezi items ⇒ this equals the plain subtotal ⇒ byte-identical.
    const nonDeziSubTotal = list.filter((l) => !isDeziLine(l, deziUpcSet)).reduce((s, l) => s + nz(l.jsonNewPrice), 0);
    if (oad !== 0) {
      if (dollarCouponExclusion) {
        // exclude_coupons_store (OCV 6066-6095): split $ proration — the non-coupon part (custom/OTD)
        // spreads over ALL non-dezi lines, the list-coupon part over coupon-eligible lines only.
        // Rounding residue is fixed by validateAmountDiscount.
        const otherDollarDiscount = oad - couponDollarDiscount;
        const otherPercentage = nonDeziSubTotal > 0 ? otherDollarDiscount / nonDeziSubTotal : 0;
        const eligibleNewSubTotal = list
          .filter((l) => isEligible(l) && !isDeziLine(l, deziUpcSet))
          .reduce((s, l) => s + nz(l.jsonNewPrice), 0);
        const couponPercentage = eligibleNewSubTotal > 0 ? couponDollarDiscount / eligibleNewSubTotal : 0;
        for (const it of list) {
          if (isDeziLine(it, deziUpcSet)) {
            it.jsonCouponCodeAmt2 = 0;
            continue;
          }
          const otherAmount = roundHalfUp(nz(it.jsonNewPrice) * otherPercentage);
          const couponAmount = isEligible(it) ? roundHalfUp(nz(it.jsonNewPrice) * couponPercentage) : 0;
          // Android per-line floor: a share can never exceed the line, nor drive it below zero.
          const dollarAmountDiscount = Math.min(roundHalfUp(otherAmount + couponAmount), nz(it.jsonNewPrice));
          it.jsonNewPrice = Math.max(roundHalfUp(nz(it.jsonNewPrice) - dollarAmountDiscount), 0);
          it.jsonCouponCodeAmt2 = dollarAmountDiscount;
        }
        // pool restricted to eligible lines only when the ENTIRE $ discount is a list coupon
        const couponOnlyDollar = roundHalfUp(oad - couponDollarDiscount) === 0;
        validateAmountDiscount(cart, mSubTotal, opd, oad, couponOnlyDollar, deziUpcSet);
      } else {
        const pct = nonDeziSubTotal > 0 ? oad / nonDeziSubTotal : 0;
        for (const it of list) {
          if (isDeziLine(it, deziUpcSet)) {
            it.jsonCouponCodeAmt2 = 0;
            continue;
          }
          const dollarAmountDiscount = Math.min(roundHalfUp(nz(it.jsonNewPrice) * pct), nz(it.jsonNewPrice));
          it.jsonNewPrice = Math.max(roundHalfUp(nz(it.jsonNewPrice) - dollarAmountDiscount), 0);
          it.jsonCouponCodeAmt2 = dollarAmountDiscount;
        }
        validateAmountDiscount(cart, mSubTotal, opd, oad, false, deziUpcSet);
      }
    }
  }

  // Stage C: loyalty proration (Phase 2; no-op when loyaltyAmount === 0)
  {
    const list = getCartListPenny(cart).filter((l) => l.canUsePoint);
    const newSubTotal = list.reduce((s, l) => s + nz(l.jsonNewPrice), 0);
    const lpPercentage = newSubTotal !== 0 ? loyaltyAmount / newSubTotal : 0;
    for (const l of cart) l.pointsPaidNcaWaived = 0;
    for (const it of list) {
      const lpDiscountAmt = roundHalfUp(nz(it.jsonNewPrice) * lpPercentage);
      // Android calculatePennyDifference LP pass: under dual price a line whose points cover its CASH
      // price is fully points-paid — its card premium is waived (hashMapPointsPaidNcaPerItem) and the
      // line nets to 0.
      if (isDual && lpDiscountAmt > 0 && lpDiscountAmt >= toBase(nz(it.jsonNewPrice))) {
        it.pointsPaidNcaWaived = roundHalfUp(nz(it.jsonNewPrice) - lpDiscountAmt);
        it.jsonNewPrice = 0;
        it.jsonLoyaltyAmt = roundHalfUp(lpDiscountAmt);
        continue;
      }
      // ⚠️ MONEY FIX: loyalty redemption covers subtotal + TAX, so on a low-priced line the prorated
      // loyalty can exceed the line's own price (lpPercentage > 1) and drive jsonNewPrice negative —
      // which then makes the per-line TAX (keyed off jsonNewPrice) negative and the whole order total
      // go below zero (a $0.99 sale fully paid by points saved as −$0.11). A line's taxable price can
      // never be less than $0, so clamp here. No-op for a normal redemption (loyalty ≤ subtotal).
      it.jsonNewPrice = Math.max(roundHalfUp(nz(it.jsonNewPrice) - lpDiscountAmt), 0);
      it.jsonLoyaltyAmt = roundHalfUp(lpDiscountAmt);
    }
    validateLpDiscount(cart, mSubTotal, opd, oad, loyaltyAmount);
  }

  // Stage D: no-discount penny fix (only when nothing order-level applied and every line has an override price)
  //
  // ⚠️ MONEY FIX (Android 2026-07-29, "refund overcharge on a price-overridden line"). This block was
  // written for a ≤1¢ ROUNDING residue and measured the gap with the GROSS override price. On a line
  // that carries BOTH an override AND a per-line discount (the only such combination the UI allows is
  // a manufacturer discount — manual $/% are mutually exclusive with an override) that gap IS the
  // discount, so it rewrote jsonNewPrice back up to ≈ the gross override. The saved `item_ses.price`
  // (= jsonNewPrice + discount) then came out as override + discount, and the refund screen — which
  // computes `price − discount_amt` — refunded MORE than was charged (device: $543.74 refunded on a
  // $543.04 sale). It also over-taxed the line, since per-line tax keys off jsonNewPrice.
  //
  // Fix (Android "form b"): measure the NET per line, using the SAME per-line item discount Stage A
  // uses, and adjust the already-net jsonNewPrice rather than the gross override. On a line with no
  // discount jsonNewPrice === the gross line total, so every term is identical to before ⇒ the
  // no-discount override path is byte-identical.
  if (opd === 0 && oad === 0 && loyaltyAmount === 0) {
    const list = getCartListPenny(cart);
    const newPriceCount = list.filter((l) => toNum(l.orderUnitNewPrice) !== 0).length;
    if (list.length === newPriceCount && list.length > 0) {
      const lineTotal = (l) => {
        const unit = toNum(l.orderUnitNewPrice);
        const count = toNum(l.orderCount);
        const pct = toNum(l.orderDiscountPercent);
        const itemDiscount = pct > 0 ? truncate2((unit * pct) / 100) : toNum(l.orderDiscount) * count;
        return roundHalfUp(unit * count - itemDiscount);
      };
      const lineItemSum = cart.reduce((s, l) => s + lineTotal(l), 0);
      const pennyAmount = roundHalfUp(lineItemSum - mSubTotal);
      let remaining = pennyAmount;
      for (const it of [...list].sort(descByUnitNew)) {
        if (remaining === 0) break;
        if (pennyAmount < 0) {
          it.jsonNewPrice = addAndRoundIfNeeded(lineTotal(it) + 0.01);
          remaining = roundHalfUp(remaining + 0.01);
        } else {
          it.jsonNewPrice = addAndRoundIfNeeded(lineTotal(it) - 0.01);
          remaining = roundHalfUp(remaining - 0.01);
        }
      }
    }
  }

  // Stage E: custom vs coupon discount split + penny reconciliation
  {
    const list = getCartListPenny(cart);
    const totalCouponDiscount = roundHalfUp(couponTotal);
    const totalCustomDiscount = roundHalfUp(customTotal);

    const getReducedPrice = (item) => {
      const unit = toNum(item.orderUnitNewPrice) > 0 ? toNum(item.orderUnitNewPrice) : toNum(item.orderUnitPrice);
      const count = toNum(item.orderCount);
      const pct = toNum(item.orderDiscountPercent);
      const itemDisc = pct > 0 ? truncate2((unit * pct) / 100) : toNum(item.orderDiscount) * count;
      return Math.max(unit * count - itemDisc, 0); // per-line
    };
    // QV-7124 rule 4: the SAVE-PASS ratios are computed over the NON-dezi reduced prices, and a dezi
    // line is never given a share. Getting this wrong is real money, not display: these two fields are
    // what the order payload carries, and Android shipped a bug where the ratio came out > 1 and a
    // single line was written a discount larger than its own price.
    // Android 10111dc9cf / 187e5f82c8 (all stores): every penny nudge of a line's coupon/custom share
    // re-derives its taxable net from the identity reducedPrice − custom − coupon − loyalty, moving it
    // by at most `step` — so the taxable amount and the net sale can never drift apart by a cent.
    const pennyMirroredNet = (item, custom, coupon, step) => {
      const net = nz(item.jsonNewPrice);
      const identity = roundHalfUp(getReducedPrice(item) - custom - coupon - nz(item.jsonLoyaltyAmt));
      const delta = Math.min(Math.max(roundHalfUp(identity - net), -step), step);
      return Math.max(addAndRoundIfNeeded(net + delta), 0);
    };
    const nonDezi = list.filter((l) => !isDeziLine(l, deziUpcSet));
    const totalReducedPrice = nonDezi.reduce((s, l) => s + getReducedPrice(l), 0);
    const customPct = totalReducedPrice > 0 ? totalCustomDiscount / totalReducedPrice : 0;
    // exclude_coupons_store (OCV 4969-4977): the list-coupon per-line share is computed over the
    // ELIGIBLE reduced prices only; OTD keeps the full-cart base. Custom is never exclusion-aware.
    const cncdCouponExclusion = hasExcluded && !isOtdCoupon && totalCouponDiscount !== 0;
    const eligibleReducedPrice = nonDezi.filter(isEligible).reduce((s, l) => s + getReducedPrice(l), 0);
    const couponPct = cncdCouponExclusion
      ? eligibleReducedPrice > 0
        ? totalCouponDiscount / eligibleReducedPrice
        : 0
      : totalReducedPrice > 0
      ? totalCouponDiscount / totalReducedPrice
      : 0;

    for (const it of list) {
      const price = getReducedPrice(it);
      if (isDeziLine(it, deziUpcSet)) {
        it.customDiscountAmt = 0;
        it.couponDiscountAmt = 0;
        continue;
      }
      // Universal backstop (Android 2026-08-03): clamp to the line's own reduced price so ANY ratio
      // greater than 1, from any cause, can never discount a line below $0. A normal ratio ≤ 1 makes
      // this a no-op ⇒ byte-identical.
      it.customDiscountAmt = Math.min(roundHalfUp(price * customPct), price);
      // an excluded line never carries a list-coupon share (OCV 4991-4995)
      it.couponDiscountAmt =
        cncdCouponExclusion && !isEligible(it) ? 0 : Math.min(roundHalfUp(price * couponPct), price);
    }

    const lineItemCoupon = list.reduce((s, l) => s + toNum(l.couponDiscountAmt), 0);
    if (roundHalfUp(totalCouponDiscount) !== roundHalfUp(lineItemCoupon)) {
      const pennyAmount = roundHalfUp(lineItemCoupon - totalCouponDiscount);
      let remaining = pennyAmount;
      // exclude_coupons_store: the coupon penny residual must never land on an excluded line; the
      // smaller pool may need bounded extra passes (default path stays single-pass).
      const couponAdjustList = (cncdCouponExclusion ? list.filter(isEligible) : list).filter(
        (l) => !isDeziLine(l, deziUpcSet)
      );
      const sorted = [...couponAdjustList].sort(descByUnitNew);
      let pass = 0;
      do {
        for (const it of sorted) {
          if (remaining === 0) break;
          if (pennyAmount < 0) {
            it.couponDiscountAmt = addAndRoundIfNeeded(toNum(it.couponDiscountAmt) + 0.01);
            remaining = roundHalfUp(remaining + 0.01);
          } else {
            it.couponDiscountAmt = addAndRoundIfNeeded(toNum(it.couponDiscountAmt) - 0.01);
            remaining = roundHalfUp(remaining - 0.01);
          }
          it.jsonNewPrice = pennyMirroredNet(it, toNum(it.customDiscountAmt), toNum(it.couponDiscountAmt), 0.01);
        }
        pass++;
      } while (cncdCouponExclusion && remaining !== 0 && pass < 8 && sorted.length > 0);
    }

    const lineItemCustom = list.reduce((s, l) => s + toNum(l.customDiscountAmt), 0);
    if (roundHalfUp(totalCustomDiscount) !== roundHalfUp(lineItemCustom)) {
      const pennyAmount = roundHalfUp(lineItemCustom - totalCustomDiscount);
      let remaining = pennyAmount;
      for (const it of [...nonDezi].sort(descByUnitNew)) {
        if (remaining === 0) break;
        if (pennyAmount < 0) {
          it.customDiscountAmt = addAndRoundIfNeeded(toNum(it.customDiscountAmt) + 0.01);
          remaining = roundHalfUp(remaining + 0.01);
        } else {
          it.customDiscountAmt = addAndRoundIfNeeded(toNum(it.customDiscountAmt) - 0.01);
          remaining = roundHalfUp(remaining - 0.01);
        }
        it.jsonNewPrice = pennyMirroredNet(it, toNum(it.customDiscountAmt), toNum(it.couponDiscountAmt), 0.01);
      }
    }
    // Final ≤1¢ identity pass over non-dezi, non-points-paid lines (Android OCVM:8138-8146).
    if (totalCouponDiscount !== 0 || totalCustomDiscount !== 0) {
      for (const it of nonDezi) {
        if (nz(it.pointsPaidNcaWaived) > 0) continue;
        it.jsonNewPrice = pennyMirroredNet(it, toNum(it.customDiscountAmt), toNum(it.couponDiscountAmt), 0.01);
      }
    }
  }

  return cart;
}
